import React, { useCallback, useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { register } from '../../../i18n';
import useDrawFloatingToolbar from '../../../hooks/useDrawFloatingToolbar';
import '../../../css/DrawFloatingToolbar.scss';

const t2 = register('AriaLabels');

const COLOR_PALETTE = [
  'TRANSPARENT', '#999999', '#FFFFFF', '#F44E3B', '#FE9200', '#FCDC00', '#DBDF00', '#A4DD00',
  '#68CCCA', '#73D8FF', '#AEA1FF', '#FDA1FF', '#333333', '#808080', '#cccccc', '#D33115',
  '#E27300', '#FCC400', '#B0BC00', '#68BC00', '#16A5A5', '#009CE0', '#7B64FF', '#FA28FF',
  '#000000', '#666666', '#B3B3B3', '#9F0500', '#C45100', '#FB9E00', '#808900', '#194D33',
  '#0C797D', '#0062B1', '#653294', '#AB149E',
];

const BRUSH_SIZES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const TRANSPARENT_CHECKERBOARD = {
  background: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 4px 4px',
};

const isTransparentColor = (color) => {
  const normalized = (color || '').toLowerCase();
  return !normalized || normalized === 'transparent';
};

const normalizeColor = (color) => (isTransparentColor(color) ? 'transparent' : color);

const DrawMenuIcon = ({ name, className = '' }) => (
  <span className={`draw-menu-icon ${name} ${className}`} aria-hidden='true' />
);

const FillColorIcon = ({ color }) => {
  const isTransparent = isTransparentColor(color);
  return (
    <span
      className='draw-floating-toolbar-color-icon fill-color-icon'
      style={isTransparent ? TRANSPARENT_CHECKERBOARD : { backgroundColor: color }}
      aria-hidden='true'
    />
  );
};

const StrokeColorIcon = ({ color }) => (
  <span
    className='draw-floating-toolbar-color-icon stroke-color-icon'
    style={{ borderColor: normalizeColor(color) === 'transparent' ? '#999' : color }}
    aria-hidden='true'
  />
);

const ColorFlyout = ({ color, onChange, onClose }) => {
  const normalized = (color || '').toLowerCase();

  return (
    <div className='draw-floating-toolbar-flyout color-flyout' role='menu'>
      <div className='draw-floating-toolbar-color-grid'>
        {COLOR_PALETTE.map((c) => {
          const value = c === 'TRANSPARENT' ? 'transparent' : c;
          const isSelected = normalized === value.toLowerCase();
          return (
            <button
              key={c}
              type='button'
              className={`draw-floating-toolbar-color-tile${isSelected ? ' selected' : ''}`}
              style={value === 'transparent' ? TRANSPARENT_CHECKERBOARD : { background: value }}
              aria-label={value}
              onClick={() => {
                onChange({ hex: value });
                onClose();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const ToolbarItem = ({
  icon,
  label,
  className = '',
  dataTool,
  collapsed,
  tooltipId,
  readOnly,
  onClick,
  children,
  showFlyout,
}) => (
  <div className='draw-floating-toolbar-flyout-anchor'>
    <button
      type='button'
      className={`draw-floating-toolbar-item drawToolbarButton ${className}`}
      data-tool={dataTool}
      data-tip={collapsed ? label : undefined}
      data-for={collapsed ? tooltipId : undefined}
      disabled={readOnly}
      onClick={readOnly ? undefined : () => {
        ReactTooltip.hide();
        onClick();
      }}
      aria-label={label}
    >
      <span className='draw-floating-toolbar-icon-wrap'>
        {children || <DrawMenuIcon name={icon} />}
      </span>
      {!collapsed && <span className='draw-floating-toolbar-label'>{label}</span>}
    </button>
    {showFlyout}
  </div>
);

const DrawFloatingToolbar = ({
  lessonElementId,
  /** Same width as the draw area — sizes the 1px dock anchor strip */
  drawWidth,
  /** Standalone: sticky on scroll. Activity/multipart: false — scrolls with content. */
  stickyScroll = true,
  /** Non-multipart activity children: start as a two-row icon bar; resize includes this layout. */
  allowHorizontal = false,
  readOnly = false,
  hasGraph = false,
  strokeColor,
  fillColor,
  shapeConstants,
  lineOptions = [],
  pointOptions = [],
  onSelect,
  onPencil,
  onBrush,
  onText,
  onCopy,
  onUndo,
  onRedo,
  onDelete,
  onStrokeColorChange,
  onFillColorChange,
  onBrushSizeChange,
  onShapeTool,
  onShapeChange,
  onLineChange,
  onPointChange,
}) => {
  const {
    toolbarRef,
    isPlaced,
    isPinned,
    isDrawInView,
    position,
    pinStyle,
    isHorizontal,
    columns,
    collapsed,
    toggleCollapsed,
    onDragStart,
    onResizeStart,
  } = useDrawFloatingToolbar(lessonElementId, { stickyScroll, allowHorizontal });

  const [openFlyout, setOpenFlyout] = useState(null);
  const [displayFillColor, setDisplayFillColor] = useState(() => normalizeColor(fillColor));
  const [displayStrokeColor, setDisplayStrokeColor] = useState(() => normalizeColor(strokeColor));

  useEffect(() => {
    setDisplayFillColor(normalizeColor(fillColor));
  }, [fillColor]);

  useEffect(() => {
    setDisplayStrokeColor(normalizeColor(strokeColor));
  }, [strokeColor]);

  const handleFillColorChange = useCallback((color) => {
    const next = normalizeColor(color.hex);
    setDisplayFillColor(next);
    onFillColorChange(color);
  }, [onFillColorChange]);

  const handleStrokeColorChange = useCallback((color) => {
    const next = normalizeColor(color.hex);
    setDisplayStrokeColor(next);
    onStrokeColorChange(color);
  }, [onStrokeColorChange]);

  const closeFlyout = useCallback(() => setOpenFlyout(null), []);

  const toggleFlyout = useCallback((name) => {
    ReactTooltip.hide();
    setOpenFlyout((prev) => (prev === name ? null : name));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!toolbarRef.current?.contains(e.target)) {
        closeFlyout();
      }
    };
    if (openFlyout) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [openFlyout, closeFlyout, toolbarRef]);

  const shapeFlyout = openFlyout === 'shapes' && (
    <div className='draw-floating-toolbar-flyout shapes-flyout' role='menu'>
      {[
        { key: 'ShapeTriangle', value: shapeConstants.TRIANGLE },
        { key: 'ShapeRectangle', value: shapeConstants.SQUARE },
        { key: 'ShapeCircle', value: shapeConstants.CIRCLE },
        { key: 'ShapeStar', value: shapeConstants.STAR },
      ].map(({ key, value }) => (
        <button
          key={key}
          type='button'
          className='draw-floating-toolbar-flyout-item'
          aria-label={key}
          onClick={() => {
            onShapeChange({ value });
            closeFlyout();
          }}
        >
          <DrawMenuIcon name={key} />
        </button>
      ))}
    </div>
  );

  const brushSizeFlyout = openFlyout === 'brushSize' && (
    <div className='draw-floating-toolbar-flyout vertical-list' role='menu'>
      {BRUSH_SIZES.map((size) => (
        <button
          key={size}
          type='button'
          className='draw-floating-toolbar-flyout-item'
          onClick={() => {
            onBrushSizeChange({ value: size });
            closeFlyout();
          }}
        >
          <span className='draw-floating-toolbar-flyout-size'>{size}</span>
        </button>
      ))}
    </div>
  );

  const lineFlyout = openFlyout === 'line' && (
    <div className='draw-floating-toolbar-flyout vertical-list draw-floating-toolbar-line-flyout' role='menu'>
      {lineOptions.map((opt) => (
        <button
          key={opt.value}
          type='button'
          className='draw-floating-toolbar-flyout-item'
          onClick={() => {
            onLineChange(opt);
            closeFlyout();
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const pointFlyout = openFlyout === 'point' && (
    <div className='draw-floating-toolbar-flyout vertical-list draw-floating-toolbar-point-flyout' role='menu'>
      {pointOptions.map((opt) => (
        <button
          key={opt.value}
          type='button'
          className='draw-floating-toolbar-flyout-item'
          onClick={() => {
            onPointChange(opt);
            closeFlyout();
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const fillFlyout = openFlyout === 'fill' && (
    <ColorFlyout
      color={displayFillColor}
      onChange={handleFillColorChange}
      onClose={closeFlyout}
    />
  );

  const strokeFlyout = openFlyout === 'stroke' && (
    <ColorFlyout
      color={displayStrokeColor}
      onChange={handleStrokeColorChange}
      onClose={closeFlyout}
    />
  );

  const toolbarStyle = {
    ...(isPinned && pinStyle ? {
      position: 'fixed',
      left: `${pinStyle.left}px`,
      top: `${pinStyle.top}px`,
    } : (isPlaced && position ? {
      left: `${position.x}px`,
      top: `${position.y}px`,
    } : null)),
  };

  const tooltipId = `draw-toolbar-tip-${lessonElementId}`;
  const anchorWidth = typeof drawWidth === 'number' ? `${drawWidth}px` : drawWidth;
  const columnClass = isHorizontal
    ? ' is-horizontal'
    : (columns === 2 ? ' is-two-column' : ' is-one-column');
  const resizeAriaLabel = allowHorizontal
    ? t2('resizeToolbar', 'Toggle toolbar size')
    : t2('resizeToolbar', 'Toggle one or two columns');

  useEffect(() => {
    if (collapsed) {
      ReactTooltip.rebuild();
    } else {
      ReactTooltip.hide();
    }
  }, [collapsed, tooltipId]);

  return (
    <div
      id={`draw-float-anchor-top-${lessonElementId}`}
      className='draw-floating-toolbar-anchor draw-float-anchor draw-float-anchor-top'
      style={{ width: anchorWidth }}
      data-draw-float-lesson={lessonElementId}
    >
    <div
      ref={toolbarRef}
      id={`drawToolbar-${lessonElementId}`}
      className={`draw-floating-toolbar${collapsed ? ' is-collapsed' : ''}${columnClass}${isPlaced && isDrawInView ? ' is-placed' : ' is-unplaced'}${isPinned ? ' is-pinned' : ''}`}
      role='toolbar'
      style={toolbarStyle}
      data-columns={isHorizontal ? 'horizontal' : columns}
    >
      <header
        className='draw-floating-toolbar-header'
        aria-label={t2('moveToolbar', 'Move toolbar')}
        onMouseDown={onDragStart}
      >
        <div className='draw-floating-toolbar-drag-handle' aria-hidden='true'>
          <DrawMenuIcon name='Move' />
        </div>
        <button
          type='button'
          className='draw-floating-toolbar-collapse-btn'
          aria-label={collapsed ? t2('showLabels', 'Show labels') : t2('hideLabels', 'Hide labels')}
          aria-expanded={!collapsed}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={toggleCollapsed}
        >
          <DrawMenuIcon name='Collapse' />
        </button>
      </header>

      <div className='draw-floating-toolbar-body'>
        <ToolbarItem
          icon='Select'
          label={t2('selectitems')}
          className='selectToolButton'
          dataTool='select'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onSelect}
        />

        <ToolbarItem
          icon='Pencil'
          label={t2('pencil')}
          className='pencilToolButton'
          dataTool='pencil'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onPencil}
        />

        <ToolbarItem
          icon='Brush'
          label={t2('brush')}
          className='paintToolButton'
          dataTool='paint'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onBrush}
        />

        <ToolbarItem
          icon='Shapes'
          label={t2('shape')}
          className='shapeToolButton'
          dataTool='shape'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={() => {
            // Parent button cancels shape placing → draw; otherwise open/close flyout.
            if (typeof onShapeTool === 'function' && onShapeTool()) {
              closeFlyout();
              return;
            }
            toggleFlyout('shapes');
          }}
          showFlyout={shapeFlyout}
        />

        <ToolbarItem
          icon='Text'
          label={t2('addtext')}
          className='textToolButton'
          dataTool='text'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onText}
        />

        <ToolbarItem
          label={t2('fillcolor', 'fill color')}
          className='fillColorButton'
          dataTool='fill'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={() => toggleFlyout('fill')}
          showFlyout={fillFlyout}
        >
          <FillColorIcon color={displayFillColor} />
        </ToolbarItem>

        <ToolbarItem
          label={t2('strokecolor')}
          className='strokeColorButton'
          dataTool='stroke'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={() => toggleFlyout('stroke')}
          showFlyout={strokeFlyout}
        >
          <StrokeColorIcon color={displayStrokeColor} />
        </ToolbarItem>

        <ToolbarItem
          icon='LineWeight'
          label={t2('brushsize')}
          className='brushSizeToolButton'
          dataTool='brushSize'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={() => toggleFlyout('brushSize')}
          showFlyout={brushSizeFlyout}
        />

        {hasGraph && (
          <ToolbarItem
            icon='GraphPoint'
            label={t2('graphpoint')}
            className='pointToolButton'
            dataTool='point'
            collapsed={collapsed}
          tooltipId={tooltipId}
            readOnly={readOnly}
            onClick={() => toggleFlyout('point')}
            showFlyout={pointFlyout}
          />
        )}

        {hasGraph && (
          <ToolbarItem
            icon='GraphLine'
            label={t2('graphline')}
            className='lineToolButton'
            dataTool='line'
            collapsed={collapsed}
          tooltipId={tooltipId}
            readOnly={readOnly}
            onClick={() => toggleFlyout('line')}
            showFlyout={lineFlyout}
          />
        )}

        <ToolbarItem
          icon='Copy'
          label={t2('copyelement')}
          className='drawCopyElementButton'
          dataTool='copy'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onCopy}
        />

        <ToolbarItem
          icon='Undo'
          label={t2('undo')}
          className='drawUndoButton'
          dataTool='undo'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onUndo}
        />

        <ToolbarItem
          icon='Redo'
          label={t2('redo')}
          className='drawRedoButton'
          dataTool='redo'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onRedo}
        />

        <ToolbarItem
          icon='Delete'
          label={t2('deleteelement')}
          className='drawDeleteElementButton'
          dataTool='delete'
          collapsed={collapsed}
          tooltipId={tooltipId}
          readOnly={readOnly}
          onClick={onDelete}
        />
      </div>

      {collapsed && (
        <ReactTooltip
          id={tooltipId}
          border
          className='react-tooltip draw-floating-toolbar-tooltip'
          delayShow={2000}
          effect='solid'
          place={isHorizontal ? 'bottom' : 'right'}
          type='light'
        />
      )}

      <footer className='draw-floating-toolbar-footer'>
        <div
          className='draw-floating-toolbar-resize-handle'
          role='separator'
          aria-label={resizeAriaLabel}
          onMouseDown={onResizeStart}
        >
          <DrawMenuIcon name='Resize' />
        </div>
      </footer>
    </div>
    </div>
  );
};

export default DrawFloatingToolbar;
